import { useState, useEffect, useMemo } from 'react'

import { Form, Field, FormElement } from '@progress/kendo-react-form'
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs'

import { FormDropDownList, FormInput, FormNumericTextBox, FormSwitch, FormTextBoxPassword } from '../FormComponents'
import { emailValidator, passwordValidator } from '../Validators'
import { getTargetEmployee, getEmployees, getCurrentUserEmployee } from '../../../data/employeeData'

import SkeletonForm from '../../Skeletons/SkeletonForm'
import { getWorktypeGroupDropdown } from '../../../data/workTypeGroupData'
import { getWorktypeDropdown } from '../../../data/worktypeData'
import { dropDown, employeesGrid } from '../../../data/interfaces'
import { getEmployeeTypes, getUserRoles, UserRoleOption } from '../../../data/userData'
import { getGroups, Group } from '../../../data/groupData'
import { MultiSelect } from '@progress/kendo-react-dropdowns'
import { Checkbox } from '@progress/kendo-react-inputs'
import { TabStrip, TabStripTab } from '@progress/kendo-react-layout'
import { canManageUniform } from '../../../data/uniformData'
import EmployeeUniformPanel from '../../../pages/Uniform/EmployeeUniformPanel'

export interface Permission { 
  area: string
  // Here, held directly on the user. Roles from groups are shown from the
  // groups picked on this form, and are never saved onto the user themselves.
  assigned: boolean
  direct?: boolean
  permission: string
  roleId: number
}

const mobilePhoneValidator = (value: string) =>
  value && /^\+?[0-9]{7,15}$/.test(value)
    ? ''
    : 'Enter a valid mobile phone number'


const EditUserForm = (props: any) => {
  //Password variables
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [passLabel, setPassLabel] = useState('')
  const [passMatch, setPassMatch] = useState(true)
  const [allowSubmit, setAllowSubmit] = useState(true)

  const [visible, setVisible] = useState(false)

  const [formState, setFormState] = useState<{
    employeeId: number;
    employeeName: string;
    position: string;
    subcontractor: boolean;
    password: string;
    adminRole: boolean;
    sortOrder: number;
    worktypegroupdefaultId: number;
    worktypegroupdefault: string;
    worktypedefaultId: number;
    worktypedefault: string;
    hourlyRate: number;
    permissions: Permission[];
    email: string
    mobilePhone: string
    type: { employeeTypeId: number, name: string }
    employeeTypeId: number
    groupIds: number[]
    directReportEmployeeIds: number[]
    canViewAllUsers: boolean
  }>({
    email: '',
    mobilePhone: '',
    employeeId: 0,
    employeeName: '',
    position: '',
    subcontractor: false,
    password: '',
    adminRole: false,
    sortOrder: 0,
    worktypegroupdefaultId: 0,
    worktypegroupdefault: '',
    worktypedefaultId: 0,
    worktypedefault: '',
    hourlyRate: 0,
    permissions: [],
    type: { employeeTypeId: 0, name: '' },
    employeeTypeId: 0,
    groupIds: [],
    directReportEmployeeIds: [],
    canViewAllUsers: false,
  })
  const [loading, setLoading] = useState(true)
  // As loaded, before any edits here: decides whether a non-administrator may
  // change this user at all.
  const [loadedAdmin, setLoadedAdmin] = useState<{ direct: boolean; groupIds: number[] }>({ direct: false, groupIds: [] })
  const employeeId = props.onEmployeeId
  const [selectedTab, setSelectedTab] = useState(0)
  // The Uniform tab is for whoever manages uniform (and administrators).
  const [showUniform, setShowUniform] = useState(false)

  useEffect(() => {
    getCurrentUserEmployee()
      .then((user) => setShowUniform(canManageUniform(user?.permissions as any)))
      .catch(() => setShowUniform(false))
  }, [])

  useEffect(() => {
    setSelectedTab(0)
  }, [employeeId])

  const toggleDeleteDialog = () => {
    setVisible(!visible)
  }

  useEffect(() => {
    const fetchPlaceholderData = async () => {
      const user = await getTargetEmployee(employeeId)
      setFormState({
        email: user.email,
        mobilePhone: user.mobilePhone ?? '',
        // The API's "assigned" includes roles from groups; only the direct ones
        // belong to the user, so only those go back on save.
        permissions: (user.permissions ?? []).map((p: Permission) => ({ ...p, assigned: p.direct ?? p.assigned })),
        employeeId: user.employeeId,
        employeeName: user.employeeName,
        position: user.position,
        subcontractor: user.subcontractor,
        password: '',
        adminRole: user.adminRole,
        sortOrder: user.sortOrder,
        worktypegroupdefaultId: user.worktypegroupDefaultId ?? 0,
        worktypegroupdefault: user.worktypegroupDefault,
        worktypedefaultId: user.worktypeDefaultId ?? 0,
        worktypedefault: user.worktypeDefault,
        hourlyRate: user.hourlyRate,
        type: user.type,
        employeeTypeId: user.employeeTypeId,
        groupIds: user.groups?.map(g => g.groupId) || [],
        directReportEmployeeIds: user.directReports?.map(e => e.employeeId) || [],
        canViewAllUsers: user.canViewAllUsers ?? false,
      })
      setLoadedAdmin({
        direct: !!user.adminRole || (user.permissions ?? []).some(
          (p: Permission) => p.assigned && `${p.area}.${p.permission}` === 'Portal.Administrator'
        ),
        groupIds: user.groups?.map(g => g.groupId) || [],
      })
      setLoading(false)
    }

    fetchPlaceholderData();
    fetchWorkTypeGroupData();
    fetchWorktypeData();
  }, [employeeId])

  const handleSubmit = () => {
    props.onEditSubmit(formState, employeeId)
  }

  const handleDelete = () => {
    props.onUserDelete(employeeId)
    toggleDeleteDialog()
  }

  const handlePasswordMatch = () => {
    if (password !== confirmPassword) {
      setPassLabel('Passwords do not match')
      setPassMatch(false)
      setAllowSubmit(false)
    } else {
      setPassLabel('')
      setPassMatch(true)
      setAllowSubmit(true)
    }
  }

  useEffect(() => {
    handlePasswordMatch()
    console.log(allowSubmit)
  }, [confirmPassword, password])

  const [workTypeGroups, setWorkTypeGroups] = useState<dropDown[]>([]);
  const fetchWorkTypeGroupData = async () => {
    const worktypeData = await getWorktypeGroupDropdown();
    setWorkTypeGroups(worktypeData);
  };

  const [worktypes, setWorktypes] = useState<dropDown[]>([]);
  const fetchWorktypeData = async () => {
    const worktypeData = await getWorktypeDropdown();
    setWorktypes(worktypeData);
  };




  const [userRoles, setUserRoles] = useState<UserRoleOption[]>([]);

  const getAndStoreUserRoles = () => {
    getUserRoles()
    .then(res => {
      setUserRoles(res);
    })
    .catch(err => console.log(err))
  }

  useEffect(() => {
    getAndStoreUserRoles()
  }, []);


  const handleRoleChange = (role: string, hasRole: boolean) => {
    setFormState((prev) => ({
      ...prev,
      permissions: prev.permissions.map((item) => {
        if(`${item.area}.${item.permission}` === role) {
          return {
            ...item,
            assigned: hasRole
          }
        } else {
          return item
        }
      }),
      adminRole: role === "Portal.Administrator" ? hasRole : prev.adminRole
    }))
  }

  const findDefaultPermission = (role: string) => {
    const foundRole = formState.permissions.find(item => `${item.area}.${item.permission}` === role);
    return foundRole ? foundRole.assigned : false;
  } 

  /** Employee type */
  const [employeeTypes, setEmployeeTypes] = useState<dropDown[]>([]);
  const [selectedEmplpyeeType, setSelectedEmployeeType] = useState(0);

  const getAndStoreEmployeeTypes = async () => {
    const res = await getEmployeeTypes();
    setEmployeeTypes(
      res.map((item: any) => {
        return {
          text: item.text,
          value: Number(item.value),
        }
      })
    )
  }

  useEffect(() => {
    getAndStoreEmployeeTypes()
  }, [])
  /** end Employee type */

  /** Groups */
  const [groups, setGroups] = useState<Group[]>([]);
  const [employeeGroups, setEmployeeGroups] = useState<Group[]>([]);

  const getAndStoreGroups = async () => {
    try {
      const res = await getGroups();
      setGroups(res);
    } catch (error) {
      console.error('Error loading groups:', error);
    }
  }

  useEffect(() => {
    getAndStoreGroups()
  }, [])

  // Update employee groups when groupIds change
  useEffect(() => {
    const selectedGroups = groups.filter(g => formState.groupIds.includes(g.groupId));
    setEmployeeGroups(selectedGroups);
  }, [formState.groupIds, groups]);

  const handleGroupChange = (event: any) => {
    const values = event.target.value.map((item: Group) => item.groupId);
    setFormState({ ...formState, groupIds: values });
  };
  /** end Groups */

  /** Direct reports */
  const [employees, setEmployees] = useState<employeesGrid[]>([]);

  useEffect(() => {
    getEmployees()
      .then(setEmployees)
      .catch((error) => console.error('Error loading employees:', error));
  }, [])

  // Everyone but this user: they always see themselves, so offering them here
  // would only put them in their own pickers twice.
  const directReportCandidates = useMemo(
    () => employees.filter(e => e.employeeId !== employeeId),
    [employees, employeeId]
  );

  const handleDirectReportsChange = (event: any) => {
    const values = event.target.value.map((item: employeesGrid) => item.employeeId);
    setFormState({ ...formState, directReportEmployeeIds: values });
  };

  // Helper function to check if a permission is inherited from groups and which groups
  const getInheritedFromGroups = (roleKey: string): string[] => {
    const groupsWithPermission: string[] = [];
    // Distribution groups grant nothing.
    employeeGroups.filter(group => group.groupType !== 'Distribution').forEach(group => {
      const hasPermission = group.roles.some(role => 
        `${role.area}.${role.permission}` === roleKey
      );
      if (hasPermission) {
        groupsWithPermission.push(group.name);
      }
    });
    return groupsWithPermission;
  };
  /** end Groups */

  // The server refuses the same: Users.View only looks, and only an
  // administrator may change an administrator or grant the role.
  const targetIsAdmin = loadedAdmin.direct || groups.some(g =>
    loadedAdmin.groupIds.includes(g.groupId) &&
    g.roles.some(role => `${role.area}.${role.permission}` === 'Portal.Administrator')
  )
  const readOnly = !props.canEdit || (!props.isAdministrator && targetIsAdmin)
  const readOnlyReason = !props.canEdit
    ? 'You have view-only access to users.'
    : 'Only a portal administrator can change an administrator\'s account.'

  // Compute the default worktype group item
  const defaultWorktypeGroupItem = useMemo(() => {
    if (workTypeGroups.length > 0 && formState.worktypegroupdefaultId > 0) {
      const found = workTypeGroups.find(wtg => {
        return Number(wtg.value) === Number(formState.worktypegroupdefaultId)
      })
      console.log('Found worktype group item:', found)
      return found || null
    }
    return null
  }, [workTypeGroups, formState.worktypegroupdefaultId])

  // Compute the default worktype item
  const defaultWorktypeItem = useMemo(() => {
    console.log('FormState worktype values:')
    console.log('formState.worktypedefaultId:', formState.worktypedefaultId, typeof formState.worktypedefaultId)
    console.log('formState.worktypedefault:', formState.worktypedefault, typeof formState.worktypedefault)
    console.log('formState.worktypegroupdefaultId:', formState.worktypegroupdefaultId, typeof formState.worktypegroupdefaultId)
    console.log('formState.worktypegroupdefault:', formState.worktypegroupdefault, typeof formState.worktypegroupdefault)

    if (worktypes.length > 0 && formState.worktypedefaultId > 0) {
      const found = worktypes.find(wt => {
        // Convert both values to numbers for comparison
        return Number(wt.value) === Number(formState.worktypedefaultId)
      })
      console.log('Found worktype item:', found)
      return found || null
    }
    return null
  }, [worktypes, formState.worktypedefaultId])



  return loading ? (
    <SkeletonForm />
  ) : (
    <div className='form__wrapper'>
      <div style={{ display: 'flex', width: '100%' }}>
       <TabStrip
         selected={selectedTab}
         onSelect={(e) => setSelectedTab(e.selected)}
         className='form__tabstrip__container'
         animation={false}
       >
        <TabStripTab title='Details'>
        <Form
          key={`${formState.employeeId}-${worktypes.length}-${formState.worktypedefaultId}`}
          initialValues={{
            employeeName: formState.employeeName,
            position: formState.position,
            subcontractor: formState.subcontractor,
            adminRole: formState.adminRole,
            sortOrder: formState.sortOrder,
            worktypegroupdefault: formState.worktypegroupdefault,
            worktypedefaultId: formState.worktypedefaultId,
            worktypeDefaultId: formState.worktypedefaultId,
            mobilePhone: formState.mobilePhone
          }}
          onSubmit={handleSubmit}
          render={(formRenderProps) => (
            <FormElement
              style={{ padding: '1rem', width: '100%', marginBottom: '5rem' }}
            >
              <fieldset className={'k-form-fieldset'} disabled={readOnly}>
                <div className='form__subtitle'>{readOnly ? 'User details:' : 'Edit user details:'}</div>
                {readOnly && <p className='k-form-hint'>{readOnlyReason}</p>}

                <Field
                  name={'employeeName'}
                  component={FormInput}
                  disabled={readOnly}
                  label={'Users name:'}
                  defaultValue={formState.employeeName}
                  onChange={(e) => {
                    setFormState({ ...formState, employeeName: e.value })
                  }}
                />

                <Field
                  name={'position'}
                  component={FormInput}
                  disabled={readOnly}
                  label={'Position: '}
                  defaultValue={formState.position}
                  onChange={(e) => {
                    setFormState({ ...formState, position: e.value })
                  }}
                />

                <Field
                  name={'hourlyrate'}
                  component={FormNumericTextBox}
                  disabled={readOnly}
                  label={'Hourly rate:'}
                  defaultValue={formState.hourlyRate}
                  placeholder={'Enter an hourly rate:'}
                  onChange={(e) => {
                    setFormState({ ...formState, hourlyRate: e.value })
                  }}
                />     

                <Field
                    data={employeeTypes}
                    name={'employeeTypeId'}
                    component={FormDropDownList}
                  disabled={readOnly}
                    label={'Type *:'}
                    required
                    defaultItem={formState.type ? employeeTypes.find(item => item.value === formState.type.employeeTypeId) : { text: '', value: 0 }}
                    onChange={(e) => {
                      setFormState({...formState, employeeTypeId: e.value.value})
                    }}
                />
                <Field
                  name={'email'}
                  component={FormInput}
                  disabled={readOnly}
                  label={'Email: '}
                  defaultValue={formState.email}
                  placeholder={'Enter an email:'}
                  validator={emailValidator}
                  onChange={(e) => {
                    setFormState({ ...formState, email: e.value })
                  }}                  
                />

                <Field
                  name={'mobilePhone'}
                  component={FormInput}
                  disabled={readOnly}
                  label={'Mobile Phone: '}
                  defaultValue={formState.mobilePhone}
                  placeholder={'Enter a mobile number:'}
                  validator={mobilePhoneValidator}
                  validationMessage="Enter a valid mobile phone number"
                  onChange={(e) => {
                    setFormState({ ...formState, mobilePhone: e.value })
                  }}
                />                          

                {!readOnly && (
                  <>
                <Field
                  name={'password'}
                  component={FormTextBoxPassword}
                  label={'Password: '}
                  onChange={(e: any) => {
                    setPassword(e.value)
                    setFormState({ ...formState, password: e.value })
                  }}
                  validator={passwordValidator}
                  type={'password'}
                  passMatch={passMatch}
                />

                <Field
                  name={'confirmPassword'}
                  component={FormTextBoxPassword}
                  label={`Confirm password: `}
                  onChange={(e: any) => setConfirmPassword(e.value)}
                  validator={passwordValidator}
                  type={'password'}
                  passLabel={passLabel}
                  passMatch={passMatch}
                />
                  </>
                )}

                <Field
                  name={'subcontractor'}
                  component={FormSwitch}
                  disabled={readOnly}
                  label={'Subcontractor: '}
                  defaultValue={formState.subcontractor}
                  onChange={(e) => {
                    setFormState({ ...formState, subcontractor: e.value })
                  }}
                />

                <div className="k-form-field" style={{ marginBottom: '1rem' }}>
                  <label className="k-label">Groups:</label>
                  <MultiSelect
                    data={groups}
                    value={groups.filter(g => formState.groupIds.includes(g.groupId))}
                    onChange={handleGroupChange}
                    disabled={readOnly}
                    textField="name"
                    dataItemKey="groupId"
                    placeholder="Select groups..."
                    style={{ width: "100%" }}
                  />
                </div>

                <div className="k-form-field" style={{ marginBottom: '1rem' }}>
                  <label className="k-label">Direct Reports:</label>
                  <MultiSelect
                    data={directReportCandidates}
                    value={directReportCandidates.filter(e => formState.directReportEmployeeIds.includes(e.employeeId))}
                    onChange={handleDirectReportsChange}
                    textField="employeeName"
                    dataItemKey="employeeId"
                    placeholder="Select employees..."
                    disabled={readOnly || formState.canViewAllUsers}
                    style={{ width: "100%" }}
                  />
                  <div style={{ marginTop: '0.5rem' }}>
                    <Checkbox
                      label="All users"
                      checked={formState.canViewAllUsers}
                      disabled={readOnly}
                      onChange={(e) => setFormState({ ...formState, canViewAllUsers: !!e.value })}
                    />
                  </div>
                  <span className="k-form-hint">
                    Anyone added here can be picked on the Notes screen and on the
                    reports to see their notes and figures. Leave empty for this
                    user's own only. Tick All users to let them see everybody -
                    their reports then start on all users rather than just themselves.
                  </span>
                </div>

                <div className="k-form-field" style={{ marginBottom: '1rem' }}>
                  <label className="k-label" style={{ marginBottom: '8px', display: 'block', fontWeight: 'bold' }}>Permissions:</label>
                  <div style={{ 
                    maxHeight: '400px', 
                    overflowY: 'auto', 
                    border: '1px solid #ccc', 
                    padding: '12px',
                    borderRadius: '4px',
                    backgroundColor: '#f9f9f9'
                  }}>
                    {
                      userRoles.length ? (
                        (() => {
                          // Group roles by area
                          const rolesByArea: { [key: string]: UserRoleOption[] } = {};
                          userRoles.forEach(role => {
                            if (!rolesByArea[role.area]) {
                              rolesByArea[role.area] = [];
                            }
                            rolesByArea[role.area].push(role);
                          });
                          
                          return Object.keys(rolesByArea).sort().map(area => (
                            <div key={area} style={{ marginBottom: '16px' }}>
                              <div style={{ 
                                fontWeight: 'bold', 
                                fontSize: '11pt',
                                color: '#1E2A3E',
                                marginBottom: '8px',
                                paddingBottom: '4px',
                                borderBottom: '1px solid #ddd'
                              }}>
                                {area}
                              </div>
                              <div style={{ 
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
                                gap: '8px',
                                marginLeft: '8px'
                              }}>
                                {rolesByArea[area].map(role => {
                                  const roleKey = `${role.area}.${role.permission}`;
                                  const inheritedGroups = getInheritedFromGroups(roleKey);
                                  const isInherited = inheritedGroups.length > 0;
                                  
                                  return (
                                    <div key={role.roleId} style={{ display: 'flex', alignItems: 'center' }}>
                                      <Checkbox
                                        value={findDefaultPermission(roleKey) || isInherited}
                                        disabled={
                                          isInherited ||
                                          readOnly ||
                                          (roleKey === 'Portal.Administrator' && !props.isAdministrator)
                                        }
                                        onChange={(e) => {
                                          handleRoleChange(roleKey, e.value || false);
                                        }}
                                        style={{ marginRight: '8px' }}
                                      />
                                      <label style={{ 
                                        fontSize: '9.5pt',
                                        cursor: isInherited ? 'not-allowed' : 'pointer',
                                        color: isInherited ? '#999' : '#000',
                                        fontStyle: isInherited ? 'italic' : 'normal'
                                      }}>
                                        {role.permission}
                                        {isInherited && (
                                          <span style={{ fontSize: '8.5pt', color: '#666', marginLeft: '4px' }}>
                                            (from {inheritedGroups.join(', ')})
                                          </span>
                                        )}
                                      </label>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          ));
                        })()
                      ) : null
                    }
                  </div>
                </div>

                <Field
                  name={'sortOrder'}
                  component={FormNumericTextBox}
                  disabled={readOnly}
                  label={'Sort order: '}
                  defaultValue={formState.sortOrder}
                  onChange={(e) => {
                    setFormState({ ...formState, sortOrder: e.value })
                  }}
                />


                <Field
                  data={workTypeGroups}
                  name={"worktypeGroupId"}
                  component={FormDropDownList}
                  disabled={readOnly}
                  label={"Choose the default group:"}
                  defaultText={formState.worktypegroupdefault}
                  defaultValue={formState.worktypegroupdefaultId}
                  onChange={(e) => {
                    setFormState({
                        ...formState,
                        worktypegroupdefaultId: parseInt(e.value.value, 10),
                    })
                  }}
                />

                <Field
                  data={worktypes}
                  name={"worktypeId"}
                  component={FormDropDownList}
                  disabled={readOnly}
                  label={"Choose the default worktype:"}
                  defaultText={formState.worktypedefault}
                  defaultValue={formState.worktypedefaultId}
                  key={`worktype-${formState.worktypedefaultId}-${formState.worktypedefault}`}
                  onChange={(e) => {
                    setFormState({
                        ...formState,
                        worktypedefaultId: parseInt(e.value.value, 10),
                        worktypedefault: e.value.text
                    })
                  }}
                />

                {!readOnly && (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    bottom: 0,
                    left: 0,
                    width: '100%',
                    padding: '1rem 2rem 1rem 2rem',
                    position: 'absolute',
                  }}
                >
                  <button
                    disabled={!allowSubmit || !formRenderProps.allowSubmit}
                    onClick={() => {
                      handleSubmit()
                      // formRenderProps.onFormReset() // Temporarily removed to test
                    }}
                    className='form__edit__button edit'
                  >
                    Save changes to User
                  </button>
                  <button
                    onClick={toggleDeleteDialog}
                    className='form__edit__button reject'
                  >
                    Delete user
                  </button>
                </div>
                )}
              </fieldset>
            </FormElement>
          )}
        />
        </TabStripTab>
        {showUniform && (
          <TabStripTab title='Uniform'>
            <EmployeeUniformPanel employeeId={employeeId} />
          </TabStripTab>
        )}
       </TabStrip>
      </div>
      {visible && (
        <Dialog title={'Please confirm'} onClose={toggleDeleteDialog}>
          <p
            style={{
              margin: '25px',
              textAlign: 'center',
            }}
          >
            Are you sure you want to delete {formState.employeeName}?
          </p>
          <DialogActionsBar>
            <button
              className='k-button k-button-md k-rounded-md k-button-solid k-button-solid-base'
              onClick={toggleDeleteDialog}
            >
              No
            </button>
            <button
              className='k-button k-button-md k-rounded-md k-button-solid k-button-solid-base'
              onClick={handleDelete}
            >
              Yes
            </button>
          </DialogActionsBar>
        </Dialog>
      )}
    </div>
  )
}

export default EditUserForm
